import React from 'react';
import styled, { css } from 'styled-components';
import { Translate, useTranslate } from '@bynder/localization';
import { token } from '@bynder/design-system';
import { fromNowTimestamp, lessThanOneSecondAgo } from '../../helpers/datetime';
import { Page, PageStates } from '../../types';
import {
  StyledPageHeaderContainer,
  StyledPageHeaderContent,
  StyledPageInfo,
} from '../shared/styled-components/Page';
import BulletPoint from '../shared/BulletPoint';
import assertNever from '../../helpers/assertNever';
import { getTypography } from '../shared/styled-components/constants';
import { TypographyHeading } from '../../types/typography';
import { EditableLabel } from '../shared/EditableLabel';
import {
  useGetThemesetColors,
  useThemesetByGuideId,
} from '../../queries/themesets';
import { useActiveChapter } from '../../queries/guides';
import { usePageMutations } from '../../mutations/pages';
import PersonalFileHeaderImage from './PersonalFileHeaderImage';
import { InspectorSetting } from '../../constants/guides';
import { useChapterMutations } from '../../mutations/widgets/chapters';
import { useGuideStore } from '../../stores/guideStore';
import { useInspectorStore } from '../../stores/inspectorStore';
import { GuideShareLinkButton } from '../guides/GuideShareLinkButton';
import { defaultTextColor, defaultWhite } from '../widgets/RichText/Config';
import { CroppedImage } from '@bynder/react-image-cropper-lib';
import { ActiveSplits, useReleaseToggle } from '../../helpers/releaseToggle';
import { PAGE_HEADER_SIZE } from '../../constants/styles';
import { PageHeaderHeights } from '../../types/headerHeights';

const PageHeader: React.FC<{
  activeGuideId: string;
  page: Page;
}> = ({ activeGuideId, page }) => {
  const { translate } = useTranslate();
  const isEditMode = useGuideStore(s => s.isEditMode);
  const setIsInspectorOpen = useInspectorStore(s => s.setIsInspectorOpen);
  const { data: themeset } = useThemesetByGuideId(activeGuideId);
  const setActiveInspectorSetting = useInspectorStore(
    s => s.setActiveInspectorSetting,
  );
  const activeInspectorSetting = useInspectorStore(
    s => s.activeInspectorSetting,
  );

  const { data: isCropperSplitActive } = useReleaseToggle(
    ActiveSplits.imageCropperRollout,
  );

  const chapter = useActiveChapter();
  const [uploadedFile, setUploadedFile] = React.useState(null);

  const customColors = useGetThemesetColors();

  const getBackgroundColor = () => {
    const fallbackColor = defaultWhite;
    const themesetBackgroundColor = themeset?.pageHeader.backgroundColor;
    const activeBackgroundColor = customColors.find(
      c => c.id === page.header.backgroundColor,
    );

    if (activeBackgroundColor) {
      return activeBackgroundColor;
    } else if (themesetBackgroundColor) {
      return themesetBackgroundColor;
    }
    return fallbackColor;
  };

  const getTextColor = () => {
    const fallbackColor = defaultTextColor;
    const typographyH2Color = themeset?.typographies[1]?.fontColor;
    const themesetTextColor = themeset?.pageHeader.textColor;
    const activeTextColor = customColors.find(
      c => c.id === page.header.textColorId,
    );
    if (activeTextColor) {
      return activeTextColor;
    } else if (themesetTextColor) {
      return themesetTextColor;
    } else if (typographyH2Color) {
      return typographyH2Color;
    }
    return fallbackColor;
  };

  const sectionWidth = page?.sections[0]?.appearance?.width;
  const sectionPaddingLeft = page?.sections[0]?.appearance?.paddingLeft;
  const sectionPaddingRight = page?.sections[0]?.appearance?.paddingRight;

  const { editPageTitle, uploadAssetInPageHeader } = usePageMutations();

  const { renameChapter } = useChapterMutations();

  const onEditChapterTitle = (newChapterTitle: string) => {
    if (chapter) {
      renameChapter.mutate({ chapterId: chapter.id, newChapterTitle });
    }
  };

  const onEditPageTitle = (newTitle: string) => {
    editPageTitle.mutate({ newTitle, pageId: page.id });
  };

  const pageTimeStamp = (
    <Translate
      id={
        page?.creationTime === page?.lastModifiedTime
          ? 'PAGE_HEADER.CREATED'
          : 'PAGE_HEADER.UPDATED'
      }
      date={
        lessThanOneSecondAgo(page?.lastModifiedTime)
          ? translate('DATETIME.JUST_NOW')
          : fromNowTimestamp(page?.lastModifiedTime)
      }
    />
  );

  const isPageEditable = isEditMode && page?.canEdit;

  const getPageStateCopy = (pageState: PageStates) => {
    switch (pageState) {
      case PageStates.DRAFT:
        return <Translate id="PAGE_HEADER.DRAFT" />;
      case PageStates.PUBLISHED:
        return <Translate id="PAGE_HEADER.PUBLISHED" />;
      default:
        assertNever(pageState);
    }
  };

  const onUploadCompleted = (mediaId: string) => {
    uploadAssetInPageHeader.mutate({ mediaId });
    setUploadedFile(null);
  };

  const handleOnClickPageHeaderContainer = () => {
    if (isEditMode) {
      setIsInspectorOpen(true);
      setActiveInspectorSetting(InspectorSetting.PAGE_VIEW);
    }
  };

  if (!page || !chapter) {
    return null;
  }

  return (
    <StyledPageHeaderContainer
      data-testid="pageHeader"
      $coverUrl={!isCropperSplitActive && page.header.asset?.imageUrl}
      $backgroundColor={getBackgroundColor().hexCode}
      onClick={handleOnClickPageHeaderContainer}
      $isActive={
        activeInspectorSetting === InspectorSetting.PAGE_VIEW && isEditMode
      }
    >
      {isCropperSplitActive && page.header.asset?.imageUrl && (
        <StyledPageHeaderWithCroppedImage
          $pageHeaderHeight={page.header.height}
        >
          <CroppedImage src={page.header.asset?.imageUrl} />
        </StyledPageHeaderWithCroppedImage>
      )}
      {isPageEditable && uploadedFile && (
        <PersonalFileHeaderImage
          uploadId={page.id}
          file={uploadedFile}
          onUploadCompleted={onUploadCompleted}
        />
      )}
      <StyledPageHeaderContent
        data-testid="pageHeaderContent"
        $pageHeaderHeight={page.header.height}
        $width={sectionWidth}
        $paddingLeft={sectionPaddingLeft}
        $paddingRight={sectionPaddingRight}
      >
        <StyledPageInfo $color={getTextColor().hexCode}>
          {page?.header?.isSubtitleVisible && (
            <p>
              {isPageEditable ? (
                <EditableLabel
                  labelText={chapter.title}
                  onChange={onEditChapterTitle}
                  height={32}
                />
              ) : (
                chapter?.title
              )}
            </p>
          )}

          {page?.header?.isTitleVisible && (
            <StyledPageTitleContainer>
              <StyledPageTitle
                $color={getTextColor().hexCode}
                $isEditMode={isEditMode}
              >
                {isPageEditable ? (
                  <EditableLabel
                    labelText={page?.title}
                    onChange={onEditPageTitle}
                    height={40}
                  />
                ) : (
                  page?.title
                )}
              </StyledPageTitle>
              {!isEditMode && (
                <StyledPageLinkButtonContainer>
                  <GuideShareLinkButton
                    guideId={activeGuideId}
                    pageId={page.id}
                    buttonText={translate('PAGE_HEADER.SHARE_BUTTON_TEXT')}
                    color={getTextColor().hexCode}
                  />
                </StyledPageLinkButtonContainer>
              )}
            </StyledPageTitleContainer>
          )}

          {isPageEditable ? (
            <span>
              {pageTimeStamp}
              <span>
                {' '}
                <BulletPoint /> {getPageStateCopy(page?.state)}
              </span>
            </span>
          ) : (
            <div />
          )}
        </StyledPageInfo>
      </StyledPageHeaderContent>
    </StyledPageHeaderContainer>
  );
};

export default PageHeader;

const StyledPageLinkButtonContainer = styled.div`
  padding-left: ${token.spacing3};
`;

export const StyledPageTitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const StyledPageTitle = styled.h2<{
  $color: string;
  $isEditMode: boolean;
}>`
  ${props => getTypography(TypographyHeading.H2, props.theme)};
  width: ${props => (props.$isEditMode ? '100%' : 'auto')};
  font-size: 3.6rem;
  line-height: 1.12em;
  margin: ${token.spacing3} 0;
  ${({ $color }) =>
    $color &&
    css`
      color: ${$color};
    `}
`;

export const StyledPageHeaderWithCroppedImage = styled.div<{
  $pageHeaderHeight: string;
}>`
  position: absolute;
  overflow: hidden;
  width: 100%;

  ${({ $pageHeaderHeight }) => {
    if ($pageHeaderHeight === PageHeaderHeights.MEDIUM) {
      return css`
        height: ${PAGE_HEADER_SIZE.MEDIUM};
      `;
    }

    if ($pageHeaderHeight === PageHeaderHeights.LARGE) {
      return css`
        height: ${PAGE_HEADER_SIZE.LARGE};
      `;
    }

    return css`
      height: ${PAGE_HEADER_SIZE.DEFAULT};
    `;
  }}
`;
